// ── CPU Perlin noise (permutation-based) ──
// Ported from curious-dusts/src/simulation/noise.ts.
// Used to drive organic flow on energy strings + Bit flicker noise.

const PERM = new Uint8Array(512);

(function initNoise() {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  // Fisher-Yates shuffle with Math.random — deterministic enough for visuals
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  for (let i = 0; i < 512; i++) PERM[i] = p[i & 255];
})();

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

function grad2d(hash: number, x: number, y: number): number {
  const h = hash & 3;
  return ((h & 1) ? -x : x) + ((h & 2) ? -y : y);
}

export function noise2d(x: number, y: number): number {
  const xi = Math.floor(x) & 255;
  const yi = Math.floor(y) & 255;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  const u = fade(xf);
  const v = fade(yf);
  const aa = PERM[PERM[xi] + yi];
  const ab = PERM[PERM[xi] + yi + 1];
  const ba = PERM[PERM[xi + 1] + yi];
  const bb = PERM[PERM[xi + 1] + yi + 1];
  return lerp(
    lerp(grad2d(aa, xf, yf), grad2d(ba, xf - 1, yf), u),
    lerp(grad2d(ab, xf, yf - 1), grad2d(bb, xf - 1, yf - 1), u),
    v,
  );
}

/**
 * Fractal Brownian Motion — sums multiple octaves of Perlin noise.
 * Returns roughly [-1, 1]. Use for organic flow fields.
 */
export function fbm(x: number, y: number, octaves: number = 3): number {
  let val = 0;
  let amp = 1;
  let freq = 1;
  let max = 0;
  for (let i = 0; i < octaves; i++) {
    val += noise2d(x * freq, y * freq) * amp;
    max += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return val / max;
}

/**
 * Low-pass filtered binary noise — outputs flicker-like pulses suitable for
 * "circuit breaker engaging" effects. Returns 0 or 1 with smoothing.
 */
export function flickerNoise(t: number, threshold: number = 0.5): number {
  const raw = noise2d(t * 8, t * 13);
  return raw > (threshold - 0.5) ? 1 : 0;
}
